import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import { ReactNode } from "react";
import { Button } from "../ui/button";
import { useTypedTranslations } from "@/i18n/typed";

const dialogVariants = cva("gap-5", {
  variants: {
    size: {
      xs: "w-[408px]",
      sm: "w-[500px]",
      md: "w-[640px]",
      lg: "w-[740px]",
      xl: "w-[940px]",
      "2xl": "w-[1200px]",
    },
  },
  defaultVariants: {
    size: "sm",
  },
});

type CustomDialogProps = VariantProps<typeof dialogVariants> & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string | ReactNode;
  description?: string | ReactNode;
  className?: string;
  classNameTitle?: string;
  children?: ReactNode;
  buttons?: ReactNode;
  stopInteractOutside?: boolean;
  cancelText?: string;
  okText?: string | React.ReactNode;
  onOkClick?: () => void;
  footerText?: string | ReactNode;
  footerTextClassName?: string;
  okButtonVariant?: "primary" | "destructive";
  disabled?: boolean;
  descriptionClassName?: string;
};

export function CustomDialog({
  open,
  onOpenChange,
  title,
  description,
  descriptionClassName,
  children,
  className,
  classNameTitle,
  size,
  buttons,
  cancelText,
  okText,
  onOkClick,
  footerText,
  footerTextClassName,
  stopInteractOutside = true,
  okButtonVariant,
  disabled = false,
}: CustomDialogProps) {
  const t = useTypedTranslations("button");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onInteractOutside={(e) => {
          if (stopInteractOutside) e.preventDefault();
        }}
        showCloseButton={false}
        className={cn(dialogVariants({ size, className }), "z-50")}
      >
        <DialogHeader>
          <DialogTitle
            className={cn(
              "text-2xl font-semibold text-gray-scale-900",
              classNameTitle,
            )}
          >
            {title}
          </DialogTitle>
          {description && (
            <DialogDescription className={cn("text-gray-700 font-medium", descriptionClassName)}>
              {description}
            </DialogDescription>
          )}
        </DialogHeader>
        {children}
        <DialogFooter className="flex items-end">
          <div className={cn("flex-1", footerTextClassName)}>{footerText}</div>
          <div className="flex space-x-3.5 justify-end">
            {buttons ? (
              buttons
            ) : (
              <>
                {cancelText !== "" && (
                  <Button
                    size="lg"
                    variant="secondary"
                    tone={"light"}
                    className="text-base font-semibold"
                    onClick={() => onOpenChange(false)}
                  >
                    {cancelText ? cancelText : t("cancel")}
                  </Button>
                )}
                {okText !== "" && (
                  <Button
                    size="lg"
                    variant={okButtonVariant}
                    disabled={disabled}
                    className="text-base font-semibold"
                    onClick={() => {
                      if (onOkClick) {
                        onOkClick();
                      } else {
                        onOpenChange(false);
                      }
                    }}
                    tone="base"
                  >
                    {okText ? okText : t("confirm")}
                  </Button>
                )}
              </>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
